import {
  Button,
  EmptyState,
  Input,
  LoadingState,
  Modal,
} from '@components/common';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deleteAccessPolicy,
  fetchSecuritySnapshot,
  saveAccessPolicy,
} from '../../../services/accessControlService';
import { selectCurrentUser } from '../../../store/auth/authSelectors';
import { fetchCurrentUser } from '../../../store/auth/authThunks';
import {
  AccessPolicySummary,
  SecuritySnapshot,
} from '../../../types/accessControl';
import { apiError, apiSuccess } from '../../../util/notify';
import { PolicyEditorModal } from './PolicyEditorModal';

const SecureLottieAnimation = require('../../../assets/lottie/secure.json');

const EMPTY_SECURITY_SNAPSHOT: SecuritySnapshot = {
  controls: [],
  policies: [],
  assignments: [],
};

export const PoliciesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const [snapshot, setSnapshot] = useState<SecuritySnapshot>(
    EMPTY_SECURITY_SNAPSHOT,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] =
    useState<AccessPolicySummary | null>(null);
  const [policyPendingDelete, setPolicyPendingDelete] =
    useState<AccessPolicySummary | null>(null);

  const companyId = currentUser?.company?.id ?? null;
  const currentUserId = currentUser?.id ?? null;

  const loadPolicies = useCallback(
    async (isRefresh = false) => {
      if (!companyId) {
        setError('Company details are missing for this account.');
        setIsLoading(false);
        return;
      }

      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const response = await fetchSecuritySnapshot(companyId);

      if (!response.success || !response.data) {
        setError(response.error || 'Failed to load access policies');
        setSnapshot(EMPTY_SECURITY_SNAPSHOT);
      } else {
        setSnapshot(response.data);
        setError(null);
      }

      setIsLoading(false);
      setIsRefreshing(false);
    },
    [companyId],
  );

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const filteredPolicies = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return snapshot.policies.filter((policy) => {
      if (!normalizedSearch) {
        return true;
      }

      return [
        policy.name,
        policy.description || '',
        ...policy.controls.map((control) => control.name),
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [searchQuery, snapshot.policies]);

  const handleCreate = () => {
    setEditingPolicy(null);
    setIsModalOpen(true);
  };

  const handleEdit = (policy: AccessPolicySummary) => {
    setEditingPolicy(policy);
    setIsModalOpen(true);
  };

  const handleSubmit = async (payload: {
    id?: string;
    name: string;
    description: string;
    controlIds: string[];
  }) => {
    if (!companyId || !currentUserId) {
      apiError('Missing company or user information for this action');
      return;
    }

    setIsSaving(true);

    const response = await saveAccessPolicy({
      id: payload.id,
      companyId,
      createdBy: currentUserId,
      name: payload.name,
      description: payload.description,
      controlIds: payload.controlIds,
    });

    setIsSaving(false);

    if (!response.success) {
      apiError(response.error || 'Failed to save policy');
      return;
    }

    apiSuccess(
      payload.id
        ? 'Policy updated successfully'
        : 'Policy created successfully',
    );
    setIsModalOpen(false);
    setEditingPolicy(null);
    await dispatch(fetchCurrentUser());
    await loadPolicies(true);
  };

  const handleDelete = (policy: AccessPolicySummary) => {
    setPolicyPendingDelete(policy);
  };

  const confirmDelete = async () => {
    if (!policyPendingDelete) {
      return;
    }

    const response = await deleteAccessPolicy(policyPendingDelete.id);

    if (!response.success) {
      apiError(response.error || 'Failed to delete policy');
      return;
    }

    apiSuccess('Policy deleted successfully');
    setPolicyPendingDelete(null);
    await dispatch(fetchCurrentUser());
    await loadPolicies(true);
  };

  if (isLoading) {
    return <LoadingState message="Loading access policies..." />;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-sm font-medium text-gray-600">Policies</p>
          <p className="mt-3 text-3xl font-semibold text-black">
            {snapshot.policies.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-sm font-medium text-gray-600">Assigned roles</p>
          <p className="mt-3 text-3xl font-semibold text-black">
            {snapshot.assignments.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-sm font-medium text-gray-600">
            Available controls
          </p>
          <p className="mt-3 text-3xl font-semibold text-black">
            {snapshot.controls.length}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">
              Company policies
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Policies are reusable bundles of controls that you can attach to
              roles for each outlet.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-80">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <Input
                value={searchQuery}
                onChange={(value) => setSearchQuery(String(value))}
                placeholder="Search policies"
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => loadPolicies(true)}
              isLoading={isRefreshing}
            >
              <RefreshCw size={16} />
              Refresh
            </Button>
            <Button onClick={handleCreate}>
              <Plus size={16} />
              Create
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {!filteredPolicies.length ? (
        <EmptyState
          title="No policies found"
          icon="lottie"
          lottieData={SecureLottieAnimation}
          description={
            searchQuery
              ? 'Try a different search term to find the policy you need.'
              : 'Create your first policy to start assigning controls to outlet roles.'
          }
          primaryAction={
            searchQuery
              ? undefined
              : {
                  label: 'Create policy',
                  onClick: handleCreate,
                }
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-app-bg/50 text-left text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-500">
                <tr>
                  <th className="px-5 py-3">Policy</th>
                  <th className="px-5 py-3">Description</th>
                  <th className="px-5 py-3">Controls</th>
                  <th className="px-5 py-3">Assignments</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPolicies.map((policy) => (
                  <tr key={policy.id} className="border-t border-gray-100">
                    <td className="px-5 py-4">
                      <div>
                        <p className="font-semibold text-black">
                          {policy.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Updated{' '}
                          {new Date(policy.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {policy.description || 'No description'}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-2">
                        {policy.controls.slice(0, 3).map((control) => (
                          <span
                            key={control.id}
                            className="rounded-full bg-app-bg px-2 py-1 text-[11px] font-semibold text-gray-700"
                          >
                            {control.name}
                          </span>
                        ))}
                        {policy.controls.length > 3 ? (
                          <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-600">
                            +{policy.controls.length - 3} more
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {policy.assignment_count} role
                      {policy.assignment_count === 1 ? '' : 's'}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(policy)}
                        >
                          <Pencil size={14} />
                          Edit
                        </Button>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => handleDelete(policy)}
                        >
                          <Trash2 size={14} />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PolicyEditorModal
        isOpen={isModalOpen}
        isSaving={isSaving}
        controls={snapshot.controls}
        policy={editingPolicy}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPolicy(null);
        }}
        onSubmit={handleSubmit}
      />

      <Modal
        isOpen={Boolean(policyPendingDelete)}
        onClose={() => setPolicyPendingDelete(null)}
        title="Delete policy"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-gray-600">
            {policyPendingDelete
              ? `Delete "${policyPendingDelete.name}" and remove all role assignments linked to it?`
              : 'Delete this policy?'}
          </p>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setPolicyPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button onClick={confirmDelete}>Delete policy</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
